import { useEffect, useState } from 'react';

/**
 * Scrollspy: returns the id of the section currently in the viewport's upper
 * band (or '' before any is in view). Shared by the Header nav and the floating
 * NavBar so both highlight the same section as the user scrolls.
 *
 * `ids` may be a fresh array on each render — the effect keys off the joined
 * ids, so it only re-subscribes when the set of sections actually changes.
 */
export function useScrollSpy(ids: readonly string[]): string {
  const [active, setActive] = useState('');
  const key = ids.join(',');

  useEffect(() => {
    const sectionIds = key ? key.split(',') : [];
    if (sectionIds.length === 0) return;

    // Track which sections are currently inside the activation band. Deriving the
    // active id from this set (instead of only ever setting it) means the
    // highlight CLEARS when you scroll back to the hero/top, where no nav section
    // is in view — fixing the "stuck highlight" bug.
    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        // First section (in nav/document order) still in the band, or '' if none.
        setActive(sectionIds.find((id) => visible.has(id)) ?? '');
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [key]);

  return active;
}
